import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client, { getSession } from "@/lib/api";
import Divider from "@/components/Divider";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const storyImg = (slug) => `${API_BASE}/api/images/brand/pantry-${slug}`;

export default function ReaderPantry() {
  const [pantry, setPantry] = useState([]);
  const [stories, setStories] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getSession()) { navigate("/login"); return; }
    Promise.all([
      client.get("/content/pantry"),
      client.get("/content/techniques"),
    ]).then(([p, t]) => {
      setPantry(p.data.pantry);
      setStories(p.data.stories);
      setTechniques(t.data.techniques);
    }).catch(() => navigate("/login"));
  }, [navigate]);

  return (
    <div className="bg-mizizi-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-6">
        <Link to="/read" data-testid="back-reader" className="overline text-mizizi-terracotta hover:text-mizizi-gold">← Table of Contents</Link>
      </div>

      <header className="max-w-4xl mx-auto px-6 md:px-10 pb-12">
        <div className="overline text-mizizi-gold">The Pantry</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl mt-4 leading-[0.95] text-mizizi-charcoal">Stock your shelves.</h1>
        <p className="mt-6 text-lg text-mizizi-charcoal/70 max-w-2xl leading-relaxed">
          Every ingredient in this book deserves knowing. Below: extended sourcing, seasonality, and substitution notes only buyers see.
        </p>
      </header>

      <Divider />

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <div className="overline text-mizizi-terracotta">Ingredient Stories</div>
        <div className="mt-10 space-y-16">
          {stories.map((s, i) => (
            <article key={s.slug} data-testid={`ingredient-${s.slug}`} className="grid md:grid-cols-12 gap-10 items-start">
              <div className="md:col-span-5 aspect-[4/5] overflow-hidden bg-mizizi-forest">
                <img
                  src={storyImg(s.slug)}
                  alt={s.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="md:col-span-7">
                <div className="overline text-mizizi-gold">{String(i + 1).padStart(2, "0")} · Ingredient</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-3 text-mizizi-charcoal">{s.title}</h2>
                <p className="mt-3 italic text-mizizi-charcoal/70 text-lg">{s.hero}</p>
                <p className="mt-4 text-mizizi-charcoal/85 leading-relaxed">{s.body}</p>

                {s.extended && (
                  <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 hairline-t pt-6">
                    <div>
                      <dt className="overline text-mizizi-terracotta">Sourcing</dt>
                      <dd className="mt-2 text-sm text-mizizi-charcoal/80 leading-relaxed">{s.extended.sourcing}</dd>
                    </div>
                    <div>
                      <dt className="overline text-mizizi-terracotta">Seasonality</dt>
                      <dd className="mt-2 text-sm text-mizizi-charcoal/80 leading-relaxed">{s.extended.seasonality}</dd>
                    </div>
                    <div>
                      <dt className="overline text-mizizi-terracotta">Substitutions</dt>
                      <dd className="mt-2 text-sm text-mizizi-charcoal/80 leading-relaxed">{s.extended.substitutions}</dd>
                    </div>
                    <div>
                      <dt className="overline text-mizizi-terracotta">Shelf life</dt>
                      <dd className="mt-2 text-sm text-mizizi-charcoal/80 leading-relaxed">{s.extended.shelf_life}</dd>
                    </div>
                    {s.extended.pair_with?.length > 0 && (
                      <div className="sm:col-span-2">
                        <dt className="overline text-mizizi-terracotta">Master these techniques</dt>
                        <dd className="mt-2 flex flex-wrap gap-2">
                          {s.extended.pair_with.map((tslug) => (
                            <Link
                              key={tslug}
                              to={`/read/techniques/${tslug}`}
                              data-testid={`pair-${tslug}`}
                              className="text-sm px-3 py-1 border border-mizizi-gold/50 text-mizizi-charcoal hover:bg-mizizi-forest hover:text-mizizi-cream transition-colors"
                            >
                              {tslug.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")}
                            </Link>
                          ))}
                        </dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-mizizi-charcoal text-mizizi-cream py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="overline text-mizizi-gold text-center">Stock the Shelves</div>
          <h2 className="font-serif text-4xl md:text-5xl text-center mt-4">The Mizizi Pantry</h2>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {pantry.map((sec, i) => (
              <div key={sec.section} data-testid={`pantry-section-${i}`}>
                <div className="overline text-mizizi-gold hairline-b pb-3">{sec.section}</div>
                <ul className="mt-4 space-y-2 font-serif text-lg">
                  {sec.items.map((it) => <li key={it}>{it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20">
        <div className="overline text-mizizi-terracotta text-center">Techniques</div>
        <h2 className="font-serif text-4xl md:text-5xl text-center mt-4 text-mizizi-charcoal">Six moves the book leans on.</h2>
        <div className="mt-12 grid sm:grid-cols-2 gap-4">
          {techniques.map((t) => (
            <Link
              key={t.slug}
              to={`/read/techniques/${t.slug}`}
              data-testid={`pantry-technique-${t.slug}`}
              className="block p-5 border border-mizizi-gold/30 hover:bg-mizizi-forest hover:text-mizizi-cream transition-colors group"
            >
              <div className="overline text-mizizi-gold">Technique</div>
              <div className="font-serif text-xl mt-1">{t.title}</div>
              <div className="text-sm text-mizizi-charcoal/60 italic mt-1 group-hover:text-mizizi-cream/70">{t.subtitle}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
